import { useState, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from '@apollo/client/react';
import { Monitor, Trash2, Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  Button,
  cn,
} from '@scio3/ui';
import { DEVICES_QUERY, UNPAIR_DEVICES } from '@scio3/graphql-client';
import { toast } from 'sonner';
import { useConfirm } from '@/components/shared';

const ITEMS_PER_PAGE = 10;

type ScreenManagementDialogProps = {
  open: boolean;
  onClose: () => void;
  /** Current total number of paired screens */
  totalPairedScreens: number;
  subAccountId?: string;
  teamId?: string;
};

interface DeviceNode {
  _id: string;
  deviceName?: string;
  path?: string;
}

interface DevicesResponse {
  devices: {
    page: {
      edges: Array<{ node: DeviceNode }>;
      pageInfo: { hasNextPage: boolean; endCursor: string };
      totalCount: number;
    };
  };
}

/**
 * Screen Management dialog matching scio1's screenManagementModal.
 * Shows when user tries to decrease subscription screen count.
 * Lists paired devices with delete (unpair) buttons.
 */
export function ScreenManagementDialog({
  open,
  onClose,
  totalPairedScreens,
  teamId,
}: ScreenManagementDialogProps) {
  const { t } = useTranslation();
  const [afterCursor, setAfterCursor] = useState<string | undefined>(undefined);
  const [currentPage, setCurrentPage] = useState(1);
  const [removingId, setRemovingId] = useState<string | null>(null);
  const confirm = useConfirm();
  // Store cursors for each page so we can navigate back
  const cursorMap = useRef<Record<number, string | undefined>>({ 1: undefined });

  const { data, loading, refetch } = useQuery<DevicesResponse>(DEVICES_QUERY, {
    variables: {
      first: ITEMS_PER_PAGE,
      after: afterCursor,
      query: { teamId },
    },
    skip: !open,
    fetchPolicy: 'network-only',
  });

  const [unpairDevices] = useMutation(UNPAIR_DEVICES, {
    refetchQueries: ['Devices', 'GetTotalScreensCount'],
  });

  const devices = data?.devices?.page?.edges?.map((e) => e.node) ?? [];
  const hasNextPage = data?.devices?.page?.pageInfo?.hasNextPage ?? false;
  const endCursor = data?.devices?.page?.pageInfo?.endCursor;
  const totalCount = data?.devices?.page?.totalCount ?? totalPairedScreens;
  const totalPages = Math.ceil(totalCount / ITEMS_PER_PAGE);

  // Store the endCursor for the next page when data loads
  if (endCursor && hasNextPage) {
    cursorMap.current[currentPage + 1] = endCursor;
  }

  const goToPage = useCallback((page: number) => {
    setCurrentPage(page);
    setAfterCursor(cursorMap.current[page]);
  }, []);

  const handleDelete = useCallback(
    async (device: DeviceNode) => {
      const result = await confirm({
        title: t('Unpair device'),
        description: t('Are you sure you want to unpair this device?'),
        confirm: { label: t('Unpair'), variant: 'destructive' },
      });
      if (result !== 'confirm') return;

      setRemovingId(device._id);
      try {
        await unpairDevices({
          variables: {
            teamId,
            payload: { deviceIds: [device._id] },
          },
        });
        toast.success(t('Device unpaired successfully'));
        refetch();
      } catch {
        toast.error(t('Failed to unpair device'));
      } finally {
        setRemovingId(null);
      }
    },
    [unpairDevices, teamId, refetch, t, confirm],
  );

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) onClose();
      }}
    >
      <DialogContent
        className="max-h-[85vh] flex flex-col overflow-hidden sm:max-w-lg"
        data-testid="screen-management-dialog"
      >
        <DialogHeader>
          <DialogTitle>{t('Screen Management')}</DialogTitle>
        </DialogHeader>

        {/* Info message */}
        <div className="text-center text-sm text-muted-foreground px-4 py-2">
          {t('screenManagementModal1', { screenNumber: String(totalCount) })}
        </div>

        {/* Device list */}
        <div className="flex-1 overflow-y-auto px-4">
          {loading && devices.length === 0 ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <table className="w-full">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-2 text-sm font-medium text-muted-foreground">
                    {t('Device Name')}
                  </th>
                  <th className="w-12" />
                </tr>
              </thead>
              <tbody>
                {devices.map((device) => (
                  <tr key={device._id} className="border-b border-border">
                    <td className="py-3 text-sm">
                      <div className="flex items-center gap-2">
                        <Monitor className="h-4 w-4 text-muted-foreground shrink-0" />
                        <span>
                          {device.path ? `${device.path}/` : ''}
                          {device.deviceName ?? device._id}
                        </span>
                      </div>
                    </td>
                    <td className="py-3 text-right">
                      <Button
                        variant="outline"
                        size="icon-sm"
                        onClick={() => handleDelete(device)}
                        disabled={removingId === device._id}
                        aria-label={t('Unpair device')}
                        className={cn(
                          'shrink-0',
                          removingId === device._id
                            ? 'opacity-50 cursor-not-allowed'
                            : 'cursor-pointer',
                        )}
                      >
                        {removingId === device._id ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Trash2 className="h-4 w-4" />
                        )}
                      </Button>
                    </td>
                  </tr>
                ))}

                {devices.length === 0 && !loading && (
                  <tr>
                    <td colSpan={2} className="py-6 text-center text-sm text-muted-foreground">
                      {t('No devices found')}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-end gap-1 py-3">
              <Button
                variant="outline"
                size="sm"
                onClick={() => goToPage(currentPage - 1)}
                disabled={currentPage <= 1}
                className={cn(
                  currentPage <= 1 ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer',
                )}
              >
                &lt;
              </Button>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                <Button
                  key={page}
                  variant={page === currentPage ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => goToPage(page)}
                  disabled={page > currentPage && !cursorMap.current[page]}
                  className={cn(
                    page > currentPage && !cursorMap.current[page]
                      ? 'opacity-50 cursor-not-allowed'
                      : 'cursor-pointer',
                    'min-w-8',
                  )}
                >
                  {page}
                </Button>
              ))}
              <Button
                variant="outline"
                size="sm"
                onClick={() => goToPage(currentPage + 1)}
                disabled={!hasNextPage}
                className={cn(!hasNextPage ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer')}
              >
                &gt;
              </Button>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="cursor-pointer">
            {t('Close')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
